import React, { useState } from 'react';
import styled from 'styled-components';
import { ColorSelect } from 'components/FormFields';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const SelectedValue = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB'};
  border-radius: 8px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};

  code {
    color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  }
`;

const colorOptions = [
  { label: 'Red', value: '#EF4444' },
  { label: 'Orange', value: '#F97316' },
  { label: 'Yellow', value: '#EAB308' },
  { label: 'Green', value: '#22C55E' },
  { label: 'Blue', value: '#3B82F6' },
  { label: 'Purple', value: '#A855F7' },
  { label: 'Pink', value: '#EC4899' },
];

const teamColorOptions = [
  { label: 'Pit Pay Orange', value: '#FA4616' },
  { label: 'Racing Red', value: '#DC2626' },
  { label: 'Checkered Black', value: '#1F2937' },
  { label: 'Victory Gold', value: '#D97706' },
  { label: 'Track Green', value: '#059669' },
  { label: 'Sky Blue', value: '#0284C7' },
];

const ColorSelectDocs = () => {
  const [basicValue, setBasicValue] = useState('');
  const [teamValue, setTeamValue] = useState('#FA4616');
  const [noEmptyValue, setNoEmptyValue] = useState('#EF4444');

  const props = [
    {
      name: 'label',
      type: 'string',
      required: false,
      description: 'Label text displayed above the select',
    },
    {
      name: 'required',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows a red asterisk next to the label',
    },
    {
      name: 'value',
      type: 'string',
      required: false,
      description: 'The selected color value (hex code)',
    },
    {
      name: 'onChange',
      type: 'function',
      required: true,
      description: 'Callback when value changes. Receives hex color string.',
    },
    {
      name: 'placeholder',
      type: 'string',
      default: '"Select a color..."',
      required: false,
      description: 'Placeholder text when no color is selected',
    },
    {
      name: 'options',
      type: 'array',
      default: '[]',
      required: false,
      description:
        'Array of {label, value} objects where value is a hex color code',
    },
    {
      name: 'helperText',
      type: 'string',
      required: false,
      description: 'Helper or error text below the select',
    },
    {
      name: 'error',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Shows error state styling',
    },
    {
      name: 'width',
      type: 'string',
      default: '"100%"',
      required: false,
      description: 'Width of the select',
    },
    {
      name: 'maxWidth',
      type: 'string',
      required: false,
      description: 'Maximum width of the select',
    },
    {
      name: 'disabled',
      type: 'boolean',
      default: 'false',
      required: false,
      description: 'Disables the select',
    },
    {
      name: 'allowEmpty',
      type: 'boolean',
      default: 'true',
      required: false,
      description: 'Whether to show a "No Color" option',
    },
    {
      name: 'emptyLabel',
      type: 'string',
      default: '"No Color"',
      required: false,
      description: 'Label for the empty/no-color option',
    },
  ];

  return (
    <>
      <PageTitle>ColorSelect</PageTitle>
      <PageDescription>
        A dropdown select for choosing colors with visual swatches. Displays
        color previews in both the trigger button and dropdown options. Supports
        an optional &quot;No Color&quot; empty option with keyboard navigation.
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic ColorSelect</SectionTitle>
        <ComponentDemo
          label="Select a color with visual swatches"
          align="stretch"
        >
          <div style={{ width: '300px', maxWidth: '100%' }}>
            <ColorSelect
              label="Highlight Color"
              options={colorOptions}
              value={basicValue}
              onChange={setBasicValue}
            />
            {basicValue && (
              <SelectedValue>
                Selected: <code>{basicValue}</code>
              </SelectedValue>
            )}
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`import { ColorSelect } from 'components/FormFields';

const colorOptions = [
  { label: 'Red', value: '#EF4444' },
  { label: 'Green', value: '#22C55E' },
  { label: 'Blue', value: '#3B82F6' },
];

const [color, setColor] = useState('');

<ColorSelect
  label="Highlight Color"
  options={colorOptions}
  value={color}
  onChange={setColor}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Pre-selected Value</SectionTitle>
        <ComponentDemo
          label="Team color with pre-selected value"
          align="stretch"
        >
          <div style={{ width: '300px', maxWidth: '100%' }}>
            <ColorSelect
              label="Team Color"
              required
              options={teamColorOptions}
              value={teamValue}
              onChange={setTeamValue}
              helperText="Choose a color for this team"
            />
            <SelectedValue>
              Value: <code>{teamValue}</code>
            </SelectedValue>
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Without Empty Option</SectionTitle>
        <ComponentDemo label='No "No Color" option' align="stretch">
          <div style={{ width: '300px', maxWidth: '100%' }}>
            <ColorSelect
              label="Required Color"
              options={colorOptions}
              value={noEmptyValue}
              onChange={setNoEmptyValue}
              allowEmpty={false}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
          {`// Disable the "No Color" option
<ColorSelect
  label="Required Color"
  options={colorOptions}
  value={color}
  onChange={setColor}
  allowEmpty={false}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Error and Disabled States</SectionTitle>
        <ComponentDemo label="Error and disabled examples" align="stretch">
          <div
            style={{
              display: 'flex',
              gap: '24px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ width: '250px' }}>
              <ColorSelect
                label="Color (Error)"
                options={colorOptions}
                value=""
                onChange={() => undefined}
                error
                helperText="Color is required"
              />
            </div>
            <div style={{ width: '250px' }}>
              <ColorSelect
                label="Color (Disabled)"
                options={colorOptions}
                value="#3B82F6"
                onChange={() => undefined}
                disabled
              />
            </div>
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock>
          {`import { ColorSelect } from 'components/FormFields';
// or
import ColorSelect from 'components/FormFields/ColorSelect';`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        <ul style={{ lineHeight: 1.8 }}>
          <li>Visual color swatches in trigger and dropdown</li>
          <li>Optional &quot;No Color&quot; empty selection</li>
          <li>Keyboard navigation (Arrow keys, Enter, Space, Escape)</li>
          <li>Accessible with ARIA combobox role</li>
          <li>Theme-aware (light/dark mode)</li>
          <li>Error state and helper text support</li>
        </ul>
      </Section>
    </>
  );
};

export default ColorSelectDocs;
